// aiAgents — En "agent" är en narrow LLM-persona med egen system-prompt
// och en tillåten delmängd av tools. Main chat delegerar till agents via
// runAgent(agentId, input, deps) och läser tillbaka deras structured
// output.
//
// Samma pattern som aiSkills (pure-planning) med tillägget att varje
// agent har en explicit tool-allowlist som AI_TOOLS-validate-steg
// respekterar. Dispatcher:n vägrar dispatcha tools som inte är i
// agent.allowedTools.

import type { ToolDefinition } from '@/lib/aiToolRegistry';
import type { ExecutionPlan } from '@/lib/aiSkills/skillRegistry';
import type { MultiSheetWorkflowState } from '@/types/workflow';

export type AgentId =
  | 'planner'
  | 'critic'
  | 'builder'
  | 'layout'
  | 'reviewer'
  | 'repo-cartographer';

/** Agent-definition. Pure metadata; ingen LLM-state. */
export interface Agent {
  id: AgentId;
  /** Kort beskrivning som visas i step-UI. */
  description: string;
  /** System-prompt som skickas med LLM-call för denna agent. */
  systemPrompt: string;
  /** Tool-names som agenten får kalla. Andra tools → dispatcher rejects. */
  allowedTools: string[];
  /** Om true: agenten får inte skriva state — tool-validate kräver sideEffect='read'. */
  readOnly: boolean;
  /** Hur många iterationer agenten max får göra innan pipeline avbryter. */
  maxIterations: number;
}

export interface AgentInput {
  /** Föregående agents output (null för första i pipeline). */
  previousOutput: unknown | null;
  /** Staging-state hittills. */
  workspace: MultiSheetWorkflowState;
  /** Shared context: user-prompt, language, etc. */
  sharedContext: {
    userPrompt: string;
    language?: 'sv' | 'en';
  };
}

export interface AgentOutput {
  agent: AgentId;
  ok: boolean;
  /** Plan eller review-result eller summary, beroende på agent. */
  payload: unknown;
  /** Kostnad i credits (estimerad eller faktisk). */
  cost: number;
  /** Tool-calls som agenten gjorde under iterationen. */
  toolCalls: Array<{ tool: string; args: Record<string, unknown> }>;
  /** Reason för non-ok. */
  error?: string;
}

const REGISTRY = new Map<AgentId, Agent>();

export function registerAgent(agent: Agent): void {
  REGISTRY.set(agent.id, agent);
}

export function getAgent(id: AgentId): Agent | null {
  return REGISTRY.get(id) ?? null;
}

export function allAgents(): Agent[] {
  return Array.from(REGISTRY.values());
}

/** Filtrera ToolDefinition[] till en agents allowlist. */
export function filterToolsForAgent(agent: Agent, tools: ToolDefinition[]): ToolDefinition[] {
  const allow = new Set(agent.allowedTools);
  return tools.filter(t => allow.has(String(t.name)));
}

/** Kontrollera om en agent får kalla en specifik tool. */
export function isToolAllowed(agent: Agent, toolName: string): boolean {
  return agent.allowedTools.includes(toolName);
}

// ── Pipeline orkestrering ─────────────────────────────────────
export interface PipelineStep {
  agentId: AgentId;
  /** Avbryt hela pipeline:n om denna agent fail:ar (vs log:a och fortsätt). */
  critical?: boolean;
}

export const DIAGRAM_PIPELINE: PipelineStep[] = [
  { agentId: 'planner', critical: true },
  { agentId: 'critic', critical: false },
  { agentId: 'builder', critical: true },
  { agentId: 'layout', critical: false },
  { agentId: 'reviewer', critical: false },
];

export interface PipelineReport {
  pipeline: PipelineStep[];
  outputs: AgentOutput[];
  ok: boolean;
  firstFailure: AgentOutput | null;
  totalCost: number;
  /** Den slutgiltiga ExecutionPlan:n som builder producerade. */
  finalPlan: ExecutionPlan | null;
}

export type AgentRunner = (agent: Agent, input: AgentInput) => AgentOutput | Promise<AgentOutput>;

/**
 * Kör hela pipeline:n. Injicerad `runner` gör den faktiska LLM-call:en
 * (tester kan använda deterministiska mocks). Varje agents output passas
 * som previousOutput till nästa.
 */
export async function runPipeline(
  steps: PipelineStep[],
  sharedContext: AgentInput['sharedContext'],
  initialWorkspace: MultiSheetWorkflowState,
  runner: AgentRunner,
): Promise<PipelineReport> {
  const outputs: AgentOutput[] = [];
  let previousOutput: unknown = null;
  const workspace = initialWorkspace;
  let firstFailure: AgentOutput | null = null;
  let finalPlan: ExecutionPlan | null = null;

  for (const step of steps) {
    const agent = REGISTRY.get(step.agentId);
    if (!agent) {
      const err: AgentOutput = {
        agent: step.agentId, ok: false, payload: null, cost: 0, toolCalls: [],
        error: `agent-not-registered: ${step.agentId}`,
      };
      outputs.push(err);
      if (!firstFailure) firstFailure = err;
      if (step.critical) break;
      continue;
    }
    const out = await runner(agent, { previousOutput, workspace, sharedContext });
    outputs.push(out);
    if (!out.ok && !firstFailure) firstFailure = out;
    if (!out.ok && step.critical) break;
    previousOutput = out.payload;
    if (agent.id === 'builder' && out.payload && typeof out.payload === 'object') {
      const maybePlan = out.payload as { plan?: ExecutionPlan };
      if (maybePlan.plan) finalPlan = maybePlan.plan;
    }
  }

  return {
    pipeline: steps,
    outputs,
    ok: outputs.every(o => o.ok),
    firstFailure,
    totalCost: outputs.reduce((sum, o) => sum + o.cost, 0),
    finalPlan,
  };
}
